import { ChangeDetectionStrategy, Component, inject, OnInit, DestroyRef, effect } from '@angular/core';
import { ActivatedRoute, NavigationEnd, Router, RouterLink, RouterOutlet } from '@angular/router';
import { AuthService } from '@tran-monorepo/frontend/auth';
import { environment } from '../environments/environment';
import { FOOTER_COPYRIGHT_MANAGEMENT_PORTAL, FOOTER_LINKS } from '@tran-monorepo/frontend/core';
import { Title } from '@angular/platform-browser';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import { filter, map, merge, switchMap, tap } from 'rxjs';
import {
    SiApplicationHeaderComponent,
    SiHeaderActionsDirective,
    SiHeaderBrandDirective,
    SiHeaderLogoDirective
} from '@siemens/element-ng/application-header';
import { SiNavbarVerticalComponent } from '@siemens/element-ng/navbar-vertical';
import { SiFooterComponent } from '@siemens/element-ng/footer';
import { UserAvatarComponent } from '@tran-monorepo/frontend/components/user-avatar';
import { SiLoadingSpinnerComponent } from '@siemens/element-ng/loading-spinner';
import { NAVBAR_VERTICAL_ITEMS } from '../shared/configs/navbar-vertical-items.config';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { SiThemeService } from '@siemens/element-ng/theme';
import { NotificationOutletComponent } from '@tran-monorepo/frontend/components';

@Component({
    selector: 'tran-root',
    templateUrl: './app.component.html',
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [
        RouterOutlet,
        NotificationOutletComponent,
        SiHeaderActionsDirective,
        SiHeaderBrandDirective,
        SiNavbarVerticalComponent,
        SiApplicationHeaderComponent,
        RouterLink,
        SiHeaderLogoDirective,
        UserAvatarComponent,
        SiFooterComponent,
        SiLoadingSpinnerComponent,
        TranslatePipe
    ]
})
export class AppComponent implements OnInit {
    protected readonly authService = inject(AuthService);
    private readonly router = inject(Router);
    private readonly translateService = inject(TranslateService);
    private readonly pageTitleService = inject(Title);
    private readonly destroyRef = inject(DestroyRef);
    private readonly activatedRoute = inject(ActivatedRoute);
    private themeService = inject(SiThemeService);

    protected readonly navbarVerticalItems = NAVBAR_VERTICAL_ITEMS;
    protected readonly footerCopyright = FOOTER_COPYRIGHT_MANAGEMENT_PORTAL;
    protected readonly footerItems = FOOTER_LINKS;
    protected readonly transformationsFondsWebsiteLink = environment.transformationsFondsWebsiteLink;
    protected userDetails = this.authService.userIdTokenClaims();

    constructor() {
        effect(() => {
            const isAuthLoading = this.authService.isAuthLoading();
            const isAdmin = this.authService.isAdmin();
            const idTokenClaims = this.authService.userIdTokenClaims();

            if (!!idTokenClaims && !isAdmin && !isAuthLoading && !this.isRouterPathUnauthorized()) {
                this.router.navigate(['/unauthorized']);
            }
        });
    }

    ngOnInit(): void {
        this.themeService.applyThemeType('dark');
        this.translatePageTitle();
    }

    protected isRouterPathUnauthorized(): boolean {
        return this.router.url.startsWith('/unauthorized');
    }

    protected isRouterPathNotFound(): boolean {
        return this.router.url.startsWith('/not-found');
    }

    private translatePageTitle(): void {
        merge(
            this.router.events.pipe(filter(event => event instanceof NavigationEnd)),
            this.translateService.onLangChange
        )
            .pipe(
                map(() => this.deepestRoute()),
                map(route => route.snapshot.data['title'] || 'management.page-titles.home'),
                switchMap(title => this.translateService.get(title)),
                takeUntilDestroyed(this.destroyRef),
                tap(translatedTitle => {
                    this.pageTitleService.setTitle(translatedTitle);
                })
            )
            .subscribe();
    }

    private deepestRoute(): ActivatedRoute {
        let route = this.activatedRoute;
        while (route.firstChild) {
            route = route.firstChild;
        }
        return route;
    }
}
